import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Trash, Upload } from 'lucide-react';
import { Value } from 'react-phone-number-input';
import { FileRejection, useDropzone } from 'react-dropzone';
import { User } from '/types/user-type';
import {
  ACCOUNT_QUERY_KEY,
  useUpdateAccount,
} from 'features/profile/hooks/use-account';
import { useQueryClient } from '@tanstack/react-query';

import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { Button } from 'components/ui/button';
import { Separator } from 'components/ui/separator';
import { Label } from 'components/ui/label';
import { Input } from 'components/ui/input';
import { CountrySelect } from 'components/ui/country-select';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from 'components/ui/form';
import UIPhoneInput from 'components/core/phone-input/phone-input';
import DummyProfile from 'assets/images/dummy_profile.png';
import { useToast } from '../../../../../hooks/use-toast';
import { useTranslation } from 'react-i18next';

/**
 * `EditProfile` component allows the user to edit their profile details, including their full name, email, phone number, and profile image.
 * It provides a form to upload a new profile image, edit personal information, and validates phone number input.
 * Once the form is successfully submitted, it triggers an update request to save the changes and navigate to the profile page.
 *
 * @component
 * @example
 * const userInfo = {
 *   fullName: 'John Doe',
 *   email: 'john.doe@example.com',
 *   phoneNumber: '+1234567890',
 *   profileImageUrl: 'http://example.com/profile.jpg',
 *   itemId: '12345'
 * };
 *
 * <EditProfile userInfo={userInfo} onClose={() => {}} />
 *
 * @param {Object} props - The component's props
 * @param {User} props.userInfo - The user information object containing current details to be edited
 * @param {Function} props.onClose - Callback function to close the dialog/modal
 *
 * @returns {React.Element} The rendered component
 */

type FormData = {
  itemId: string;
  fullName: string;
  email: string;
  phoneNumber: string;
  country: string;
  profileImageUrl: File | string;
  profileImageId: string;
};

type EditProfileProps = {
  userInfo: User;
  onClose: () => void;
};

export const EditProfile: React.FC<EditProfileProps> = ({ userInfo, onClose }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  const { mutate: updateAccount, isPending } = useUpdateAccount({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ACCOUNT_QUERY_KEY });
      queryClient.refetchQueries({ queryKey: ACCOUNT_QUERY_KEY });
      onClose();
      navigate('/profile');
    },
  });

  const [previewImage, setPreviewImage] = useState<string>(DummyProfile);
  const [isFormChanged, setIsFormChanged] = useState(false);

  const form = useForm<FormData>({
    defaultValues: {
      itemId: '',
      fullName: '',
      email: '',
      phoneNumber: '',
      country: '',
      profileImageUrl: '',
    },
  });

  const { watch, setValue, handleSubmit, control } = form;
  const watchedValues = watch();

  useEffect(() => {
    if (userInfo) {
      setValue('fullName', `${userInfo.firstName ?? ''} ${userInfo.lastName ?? ''}`.trim());
      setValue('email', userInfo.email ?? '');
      setValue('phoneNumber', userInfo.phoneNumber ?? '');
      setValue('country', userInfo.country ?? '');
      setValue('itemId', userInfo.itemId ?? '');
      setValue('profileImageUrl', userInfo.profileImageUrl || '');
      setPreviewImage(userInfo.profileImageUrl || DummyProfile);
    }
  }, [userInfo, setValue]);

  useEffect(() => {
    const initialValues = {
      fullName: `${userInfo.firstName} ${userInfo.lastName}`,
      phoneNumber: userInfo.phoneNumber,
      country: userInfo.country || '',
      profileImageUrl: userInfo.profileImageUrl || '',
    };

    setIsFormChanged(
      watchedValues.fullName !== initialValues.fullName ||
      watchedValues.phoneNumber !== initialValues.phoneNumber ||
      watchedValues.country !== initialValues.country ||
      watchedValues.profileImageUrl !== initialValues.profileImageUrl
    );
  }, [watchedValues, userInfo]);

  const onSubmit = async (data: FormData) => {
    const spaceIndex = data.fullName.indexOf(' ');
    const firstName = data.fullName.substring(0, spaceIndex);
    const lastName = data.fullName.substring(spaceIndex + 1);
    let profileImageUrl = '';

    if (typeof data.profileImageUrl === 'object') {
      const file = data.profileImageUrl as File;
      profileImageUrl = await convertFileToBase64(file);
    } else {
      profileImageUrl = data.profileImageUrl;
    }

    const payload = {
      itemId: data.itemId,
      firstName: firstName || '',
      lastName: lastName || '',
      email: data.email,
      phoneNumber: data.phoneNumber,
      country: data.country,
      profileImageUrl,
    };

    updateAccount(payload);
    onClose();
    navigate('/profile');
  };

  const handleRemoveImage = () => {
    setValue('profileImageUrl', '');
    setPreviewImage(DummyProfile);
  };

  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error('Failed to read file as Base64'));
    });
  };

  const onDrop = (acceptedFiles: File[], fileRejections: FileRejection[]) => {
    const file = acceptedFiles[0];
    if (fileRejections.length > 0) {
      toast({
        variant: 'destructive',
        title: t('UPLOAD_TO_LOCAL_STORAGE_FAILED'),
        description: `${t('UNSUPPORTED_FILE')}`,
      });
      return;
    }
    if (file.size > 512 * 1024) {
      toast({
        variant: 'destructive',
        title: t('UPLOAD_TO_LOCAL_STORAGE_FAILED'),
        description: `${t('MAX_SIZE_PROFILE_IMAGE')} (${file.name}) .`,
      });
      return;
    }
    if (file) {
      setValue('profileImageUrl', file);
      setPreviewImage(URL.createObjectURL(file));
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': [],
      'image/png': [],
    },
    multiple: false,
  });

  return (
    <DialogContent className="rounded-md sm:max-w-[700px] overflow-y-auto max-h-screen">
      <DialogHeader>
        <DialogTitle>{t('EDIT_PROFILE_TITLE')}</DialogTitle>
        <DialogDescription>{t('EDIT_PROFILE_DESCRIPTION')}</DialogDescription>
      </DialogHeader>
      <Form {...form}>
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
          <div className="flex items-start sm:items-center">
            <img
              src={previewImage}
              alt="Profile"
              className="w-[90px] h-[90px] sm:w-[100px] sm:h-[100px] rounded-full object-cover border shadow-sm"
            />
            <div className="flex flex-col gap-2 ml-4 sm:ml-9">
              <h1 className="text-xl font-semibold">
                {userInfo.firstName} {userInfo.lastName}
              </h1>
              <p className="text-sm">{t('PROFILE_IMAGE_RESTRICTION')}</p>
              <div className="flex gap-2 sm:gap-4">
                <div {...getRootProps()} className="inline-block">
                  <Button size="sm" variant="outline" type="button">
                    <Upload className="w-4 h-4" />
                    <Label className="text-xs font-medium cursor-pointer">
                      {t('UPLOAD_IMAGE')}
                    </Label>
                    <input {...getInputProps()} className="hidden" />
                  </Button>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  type="button"
                  onClick={handleRemoveImage}
                  className="text-destructive hover:text-destructive"
                >
                  <Trash className="w-4 h-4" />
                  {t('REMOVE')}
                </Button>
              </div>
            </div>
          </div>
          <Separator />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              control={control}
              name="fullName"
              rules={{
                required: t('FULL_NAME_REQUIRED'),
                minLength: { value: 2, message: t('FULL_NAME_TOO_SHORT') },
                maxLength: { value: 30, message: t('FULL_NAME_TOO_LONG') },
                pattern: {
                  value: /^[A-Za-zÀ-ÖØ-öø-ÿ]/,
                  message: t('FULL_NAME_INVALID_FORMAT'),
                },
              }}
              render={({ field }) => (
                <FormItem className="col-span-1 sm:col-span-2">
                  <Label>{t('FULL_NAME')}*</Label>
                  <FormControl>
                    <Input {...field} placeholder={t('ENTER_YOUR_FULL_NAME')} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <Label>{t('EMAIL')}</Label>
                  <FormControl>
                    <Input {...field} disabled />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="phoneNumber"
              // rules={{
              //   validate: (value) => {
              //     if (!value) return t('PHONE_NUMBER_REQUIRED');
              //     if (!isPossiblePhoneNumber(value)) return t('PHONE_NUMBER_LENGTH_INVALID');
              //     if (!isValidPhoneNumber(value)) return t('INVALID_PHONE_NUMBER');
              //     return true;
              //   },
              // }}
              render={({ field }) => (
                <FormItem>
                  <Label>{t('MOBILE_NO')}</Label>
                  <FormControl>
                    <UIPhoneInput
                      {...field}
                      onChange={(value: Value) => setValue('phoneNumber', value ?? '')}
                      placeholder={t('ENTER_YOUR_MOBILE_NUMBER')}
                      defaultCountry="CH"
                      countryCallingCodeEditable={false}
                      international
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="country"
              rules={{ required: t('COUNTRY_REQUIRED') || 'Country is required' }}
              render={({ field }) => (
                <FormItem>
                  <Label>{t('COUNTRY')}*</Label>
                  <FormControl>
                    <CountrySelect
                      value={field.value}
                      onChange={field.onChange}
                      placeholder={t('SELECT_COUNTRY') || 'Select your country'}
                      className="rounded-md"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <DialogFooter className="mt-5 flex justify-end gap-2">
            <Button variant="outline" type="button" onClick={onClose}>
              {t('CANCEL')}
            </Button>
            <Button type="submit" loading={isPending} disabled={isPending || !isFormChanged}>
              {t('SAVE')}
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </DialogContent>
  );
};
